import { html } from 'lit';
import { converse, _converse, api, u, EmojiPicker } from '@converse/headless';
import { CustomElement } from 'shared/components/element.js';
import { until } from 'lit/directives/until.js';
import './styles/reaction-panel.scss';

const { stx } = converse.env;
const { initStorage } = u;

// imgSrc: path relative to api.settings.get('assets_path'), for custom emojis
const QUICK_REACTIONS = [
    { unicode: '👍' },
    { unicode: '❤️' },
    { unicode: '🔥' },
    { unicode: '😂' },
    { unicode: '😮' },
    { unicode: '😢' },
    { unicode: '🙏' },
    { unicode: '👏' },
    { unicode: '🫡', imgSrc: 'images/custom_emojis/1fae1.png' },
];

class ReactionPanel extends CustomElement {
    static get properties() {
        return {
            model: { type: Object },
            expanded: { type: Boolean },
            render_emojis: { type: Boolean },
        };
    }

    constructor() {
        super();
        this.model = null;
        this.expanded = false;
        this.render_emojis = false;
        this.state = null;
        this.init_promise = null;
        this._btnRect = null;
        this._isMe = false;
        this._initialTop = null;
        this._onDocMouseDown = (e) => {
            if (!this.contains(e.target)) {
                this.remove();
            }
        };
    }

    connectedCallback() {
        super.connectedCallback();
        this.setAttribute('purpose', 'reaction');
        setTimeout(() => document.addEventListener('mousedown', this._onDocMouseDown), 0);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('mousedown', this._onDocMouseDown);
    }

    initEmojiState() {
        if (!this.init_promise) {
            this.init_promise = (async () => {
                await api.emojis.initialize();
                const bare_jid = _converse.session.get('bare_jid');
                const chatbox = this.model?.collection?.chatbox;
                const jid = chatbox?.get('jid') || bare_jid;
                const id = `converse.emoji-${bare_jid}-${jid}`;
                this.state = new EmojiPicker({ id });
                initStorage(this.state, id);
                await new Promise((resolve) => this.state.fetch({ success: resolve, error: resolve }));
                this.state.set({ autocompleting: null, ac_position: null });
            })();
        }
        return this.init_promise;
    }

    render() {
        return html`<div class="reaction-panel"><div class="reaction-panel__strip">${QUICK_REACTIONS.map(
                        (r) => html`<button class="reaction-panel__quick-emoji" @click="${(e) => {
                                    e.stopPropagation();
                                    this._onQuickReaction(r.unicode);
                                }}">${r.imgSrc
                                    ? html`<img class="emoji" src="${(api.settings.get('assets_path') || '').replace(/\/$/, '')}/${r.imgSrc}" alt="${r.unicode}" style="width:1.4em;height:1.4em;vertical-align:middle">`
                                    : r.unicode}</button>`,
                    )} <button class="reaction-panel__expand-btn ${this.expanded ? 'is-active' : ''}" title="More reactions" @click="${(e) => {
                            e.stopPropagation();
                            this._toggleExpand();
                        }}"><svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg></button></div>${this.expanded
                    ? html`<div class="reaction-panel__picker" @mousedown="${(/** @type {MouseEvent} */ e) => {
                                  const emojiEl = /** @type {HTMLElement} */ (e.target).closest('[data-emoji]');
                                  if (!emojiEl) return;
                                  // Prevent the click from firing so insertEmoji doesn't also run.
                                  e.preventDefault();
                                  const shortname = emojiEl.getAttribute('data-emoji') || '';
                                  const unicodeEmoji = u.shortnamesToUnicode(shortname);
                                  if (unicodeEmoji && !unicodeEmoji.includes(':')) {
                                      this._sendReaction(unicodeEmoji);
                                  }
                                  this.remove();
                              }}" @input="${(/** @type {InputEvent} */ e) => {
                                  const input = /** @type {HTMLInputElement|null} */ (
                                      /** @type {HTMLElement} */ (e.target).closest('.emoji-search')
                                  );
                                  if (!input || !this.state) return;
                                  // Update via state model (triggers onModelChanged in EmojiPicker)
                                  this.state.set({ query: input.value });
                                  // Also set directly on the picker in case the state listener
                                  // wasn't wired (this.state was null when firstUpdated ran)
                                  const picker = /** @type {any} */ (this.querySelector('converse-emoji-picker'));
                                  if (picker) picker.query = input.value;
                              }}">${until(
                                  this.initEmojiState().then(
                                      () => html`<converse-emoji-picker .state="${this.state}" .model="${this.model}" @emojiSelected="${() => this.remove()}" ?render_emojis="${this.render_emojis}" current_category="${this.state.get('current_category') || ''}" current_skintone="${this.state.get('current_skintone') || ''}" query="${this.state.get('query') || ''}"></converse-emoji-picker>`,
                                  ),
                                  html`<div class="reaction-panel__loading"></div>`,
                              )}</div>`
                    : ''}</div>`;
    }

    _onQuickReaction(unicodeEmoji) {
        this._sendReaction(unicodeEmoji);
        this.remove();
    }

    _sendReaction(unicodeEmoji) {
        const messageModel = this.model;
        if (!messageModel) return;

        const chatbox = messageModel.collection?.chatbox;
        const to_jid = chatbox?.get('jid');
        const type = messageModel.get('type') === 'groupchat' ? 'groupchat' : 'chat';
        const muc_stanza_id = type === 'groupchat' && to_jid
            ? messageModel.get(`stanza_id ${to_jid}`)
            : null;
        const msgid = muc_stanza_id
            || messageModel.get('origin_id')
            || messageModel.get('stanza_id')
            || messageModel.get('msgid');
        const nick =
            sessionStorage.getItem('s_fullname') || api.connection.get().jid.split('@')[0];

        if (!to_jid || !msgid) return;

        const stanza = stx`
            <message xmlns="jabber:client" to="${to_jid}" type="${type}" id="${u.getUniqueId()}">
                <origin-id xmlns="urn:xmpp:sid:0" id="${u.getUniqueId()}"/>
                <reactions xmlns="urn:xmpp:reactions:0" id="${msgid}">
                    <reaction>${unicodeEmoji}</reaction>
                </reactions>
                <customize xmlns="p1:push:customize" nick="${nick}">
                    <body>${unicodeEmoji}</body>
                </customize>
                <x xmlns="p1:push:custom" key="aps.mutable-content" value="1"/>
                <x xmlns="p1:push:custom" key="aps.thread-id" value="${to_jid}"/>
                <x xmlns="p1:push:custom" key="reaction_to" value="${msgid}"/>
                <x xmlns="p1:push:custom" key="reaction" value="${unicodeEmoji}"/>
                <no-copy xmlns='urn:xmpp:hints'/>
                <no-store xmlns='urn:xmpp:hints'/>
                <store xmlns="urn:xmpp:hints"/>
            </message>`;

        api.send(stanza);

        // For MUC, the server echo arrives immediately and parseMUCMessage updates the UI.
        // Local optimistic update only for direct chat (no echo mechanism there).
        if (type !== 'groupchat') {
            const own_jid = _converse.bare_jid;
            let reactions = JSON.parse(JSON.stringify(messageModel.get('reactions') || {}));
            Object.keys(reactions).forEach((emoji) => {
                reactions[emoji] = (reactions[emoji] || []).filter((jid) => jid !== own_jid);
                if (reactions[emoji].length === 0) delete reactions[emoji];
            });
            if (!reactions[unicodeEmoji]) reactions[unicodeEmoji] = [];
            reactions[unicodeEmoji].push(own_jid);
            messageModel.set('reactions', reactions);
            messageModel.trigger('change:reactions');
        }
    }

    async _toggleExpand() {
        this.expanded = !this.expanded;
        if (this.expanded) {
            await this.initEmojiState();
            this.render_emojis = true;
        }
        this.requestUpdate();
        await this.updateComplete;
        // Double rAF: parent renders on first, child (converse-emoji-picker) on second
        await new Promise((r) => requestAnimationFrame(r));
        await new Promise((r) => requestAnimationFrame(r));
        this._applyPosition();

        if (this.expanded) {
            this._bindSearchInput();
        }
    }

    _bindSearchInput(attempt = 0) {
        const input = /** @type {HTMLInputElement|null} */ (this.querySelector('.emoji-search'));
        if (!input) {
            if (attempt < 5) setTimeout(() => this._bindSearchInput(attempt + 1), 80);
            return;
        }
        const inputAny = /** @type {any} */ (input);
        if (inputAny._reactionBound) return;
        inputAny._reactionBound = true;
        input.addEventListener('input', () => {
            const query = input.value;
            const picker = /** @type {any} */ (this.querySelector('converse-emoji-picker'));
            const content = /** @type {any} */ (this.querySelector('converse-emoji-picker-content'));
            console.log('[ReactionPanel] input fired, query=', query, 'state=', this.state, 'picker=', picker);
            if (this.state) {
                this.state.set({ query });
                console.log('[ReactionPanel] after state.set, state.query=', this.state.get('query'));
            }
            if (picker) {
                picker.query = query;
                console.log('[ReactionPanel] after picker.query= picker.query=', picker.query, 'picker.search_results=', picker.search_results?.length);
            }
            if (content) {
                content.query = query;
                content.requestUpdate();
                // Directly toggle visibility — bypasses all CSS specificity issues
                const browse = /** @type {HTMLElement|null} */ (content.querySelector('.emoji-lists__container--browse'));
                const searchEl = /** @type {HTMLElement|null} */ (content.querySelector('[class*="lists__container--search"]'));
                if (browse) browse.style.display = query ? 'none' : '';
                if (searchEl) searchEl.style.display = query ? 'block' : 'none';
                console.log('[ReactionPanel] browse=', browse, 'display=', browse?.style.display, '| searchEl=', searchEl, 'display=', searchEl?.style.display);
            }
        });
    }

    /**
     * Position the panel relative to the reaction button's bounding rect.
     * Stores top so subsequent expand calls keep the strip in place.
     * @param {DOMRect} btnRect - getBoundingClientRect() of the reaction button
     * @param {boolean} isMe - true for outgoing messages
     */
    positionNear(btnRect, isMe) {
        this._btnRect = btnRect;
        this._isMe = isMe;
        this._initialTop = null; // reset so top is recalculated fresh
        this._applyPosition();
    }

    _applyPosition() {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const gap = 8;
        const btnRect = this._btnRect;
        if (!btnRect) return;

        const panelEl = /** @type {HTMLElement|null} */ (this.firstElementChild);
        const panelW = panelEl?.offsetWidth || 380;

        // Calculate top only once (when collapsed). On expand the picker grows
        // downward so the strip stays at the same position.
        if (this._initialTop === null) {
            const stripH = 56;
            let top = btnRect.top - stripH - 8;
            if (top < gap) top = btnRect.bottom + 8;
            this._initialTop = Math.max(gap, Math.min(top, vh - stripH - gap));
        }

        let left;
        if (this._isMe) {
            left = btnRect.left;
        } else {
            left = btnRect.right - panelW;
        }
        left = Math.max(gap, Math.min(left, vw - panelW - gap));

        this.style.cssText = `position:fixed;top:${this._initialTop}px;left:${left}px;z-index:9999;`;
    }
}

api.elements.define('converse-reaction-panel', ReactionPanel);
export default ReactionPanel;
